import { supabase } from '@/integrations/supabase/client';

export interface CurrentProjectAccessContext {
  memberId: string | null;
  roleCodes: string[];
  isAdmin: boolean;
  isGuest: boolean;
}

export async function getCurrentProjectAccessContext(
  orgId: string
): Promise<CurrentProjectAccessContext> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return { memberId: null, roleCodes: [], isAdmin: false, isGuest: false };
  }

  const { data: member, error: memberError } = await supabase
    .from('core_organization_members')
    .select('id')
    .eq('org_id', orgId)
    .eq('user_id', user.id)
    .eq('status', 'active')
    .single();

  if (memberError || !member) {
    return { memberId: null, roleCodes: [], isAdmin: false, isGuest: false };
  }

  const { data: roles, error: rolesError } = await supabase
    .from('core_member_roles')
    .select('role:core_roles(code)')
    .eq('member_id', member.id);

  if (rolesError) {
    console.warn('[projectAccessContextRepository] Unable to resolve current roles:', rolesError.message);
  }

  const roleCodes = (roles || [])
    .map((row: any) => row.role?.code)
    .filter((code: unknown): code is string => typeof code === 'string');

  const isAdmin = roleCodes.includes('owner') || roleCodes.includes('admin');
  const isGuest = !isAdmin && roleCodes.includes('guest');

  return {
    memberId: member.id,
    roleCodes,
    isAdmin,
    isGuest,
  };
}

export async function getExplicitlySharedProjectIds(
  memberId: string,
  projectIds: string[]
): Promise<Set<string>> {
  const uniqueProjectIds = [...new Set(projectIds)];
  if (uniqueProjectIds.length === 0) {
    return new Set();
  }

  const { data, error } = await supabase
    .from('project_permissions')
    .select('project_id')
    .eq('org_member_id', memberId)
    .in('project_id', uniqueProjectIds);

  if (error) {
    console.warn('[projectAccessContextRepository] Unable to resolve explicit project shares:', error.message);
    return new Set();
  }

  return new Set((data || []).map((row) => row.project_id));
}

export async function getSharedSpaceIds(
  memberId: string,
  spaceIds: string[]
): Promise<Set<string>> {
  const uniqueSpaceIds = [...new Set(spaceIds)];
  if (uniqueSpaceIds.length === 0) {
    return new Set();
  }

  const { data, error } = await supabase
    .from('space_members')
    .select('space_id')
    .eq('org_member_id', memberId)
    .in('space_id', uniqueSpaceIds);

  if (error) {
    if (error.code === 'PGRST205' || error.code === '42P01') {
      console.warn('[projectAccessContextRepository] space_members table not found, skipping shared spaces');
      return new Set();
    }

    console.warn('[projectAccessContextRepository] Unable to resolve shared spaces:', error.message);
    return new Set();
  }

  return new Set((data || []).map((row) => row.space_id));
}

export async function getParentSpaceIdsForExplicitProjectShares(
  memberId: string
): Promise<Set<string>> {
  const { data: permissionRows, error: permissionsError } = await supabase
    .from('project_permissions')
    .select('project_id')
    .eq('org_member_id', memberId);

  if (permissionsError) {
    console.warn('[projectAccessContextRepository] Unable to resolve parent spaces for explicit project shares:', permissionsError.message);
    return new Set();
  }

  const projectIds = [...new Set((permissionRows || []).map((row) => row.project_id))];
  if (projectIds.length === 0) {
    return new Set();
  }

  const { data: projects, error: projectsError } = await supabase
    .from('project_projects')
    .select('space_id')
    .in('id', projectIds);

  if (projectsError) {
    console.warn('[projectAccessContextRepository] Unable to resolve parent spaces from shared projects:', projectsError.message);
    return new Set();
  }

  return new Set(
    (projects || [])
      .map((project) => project.space_id)
      .filter((spaceId): spaceId is string => typeof spaceId === 'string' && spaceId.length > 0)
  );
}
